// LoginWithOtpPage.js
import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import {
  Shield,
  Mail,
  ArrowRight,
  ArrowLeft
} from 'lucide-react';
import LoginLeftSide from '../components/LoginLeftSide';
// import Logo from './logo.png'; // (Optional: if you want to use a logo image)

//
// Styled Components
//

// Overall page container
const PageContainer = styled.div`
  min-height: 100vh;
  display: flex;
`;

// -------------------- Left Section --------------------
const LeftSection = styled.div`
  display: none;
  @media (min-width: 1024px) {
    display: flex;
    width: 50%;
    padding: 4rem;
    flex-direction: column;
    justify-content: space-between;
    color: white;
    position: relative;
    overflow: hidden;
    background: linear-gradient(180deg, #1a181c, #533d66);
  }
`;

const LeftTop = styled.div`
  position: relative;
  z-index: 10;
`;

const BrandRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 0.75rem;
`;

const IconBox = styled.div`
  padding: 0.75rem;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 0.75rem;
  backdrop-filter: blur(4px);
`;

const BrandText = styled.div`
  span {
    font-size: 1.5rem;
    font-weight: bold;
    letter-spacing: -0.025em;
  }
  p {
    font-size: 0.875rem;
    color: rgba(255, 255, 255, 0.8);
  }
`;

const LeftBottom = styled.div`
  position: relative;
  z-index: 10;
`;

const BigHeading = styled.h1`
  font-size: 3.75rem;
  font-weight: bold;
  line-height: 1;
  margin-bottom: 2rem;
`;

const GradientText = styled.span`
  background: linear-gradient(to right, #d8b4fe, #f472b6);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const UnderlineText = styled.span`
  position: relative;
  &::after {
    content: "";
    position: absolute;
    bottom: 0;
    left: 0;
    width: 100%;
    height: 0.25rem;
    background: rgba(168, 85, 247, 0.5);
    border-radius: 9999px;
  }
`;

const LeftParagraph = styled.p`
  font-size: 1.125rem;
  color: rgba(255, 255, 255, 0.9);
  line-height: 1.5;
  margin-bottom: 2.5rem;
  max-width: 36rem;
`;

const LearnMoreLink = styled.a`
  display: inline-flex;
  align-items: center;
  padding: 0.75rem 1.5rem;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 9999px;
  color: white;
  text-decoration: none;
  backdrop-filter: blur(4px);
  transition: background 0.3s ease, transform 0.3s ease;
  &:hover {
    background: rgba(255, 255, 255, 0.2);
  }
  svg {
    margin-left: 0.5rem;
    transition: transform 0.3s ease;
  }
  &:hover svg {
    transform: translateX(0.25rem);
  }
`;

// -------------------- Right Section --------------------
const RightSection = styled.div`
  width: 100%;
  background: white;
  @media (min-width: 1024px) {
    width: 50%;
  }
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  @media (min-width: 768px) {
    padding: 4rem;
  }
`;

const FormWrapper = styled.div`
  width: 100%;
  max-width: 28rem;
`;

const FormHeader = styled.div`
  text-align: center;
  margin-bottom: 3rem;
`;

const FormTitle = styled.h2`
  font-size: 1.875rem;
  font-weight: bold;
  color: #1f2937;
  margin-bottom: 0.75rem;
`;

const FormSubtitle = styled.p`
  font-size: 1rem;
  color: #4b5563;
`;

// Card container for the form content
const Card = styled.div`
  background: white;
  border-radius: 0.75rem;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.1);
  padding: 2rem;
`;

// -------------------- OTP Form Components --------------------
const OTPInputsContainer = styled.div`
  display: flex;
  gap: 0.75rem;
  justify-content: space-between;
`;

const OTPInput = styled.input`
  width: 3.5rem;
  height: 3.5rem;
  text-align: center;
  border: 2px solid #e5e7eb;
  border-radius: 0.75rem;
  font-size: 1.25rem;
  font-weight: 600;
  background: #f9fafb;
  outline: none;
  transition: all 0.2s ease;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  &:focus {
    border-color: #9333ea;
    box-shadow: 0 0 0 2px #e9d5ff;
  }
  &:hover {
    border-color: #a78bfa;
  }
`;

const ResendContainer = styled.div`
  text-align: center;
  font-size: 0.875rem;
  color: #6b7280;
`;

const ResendButton = styled.button`
  background: none;
  border: none;
  color: #533d66;
  font-weight: 600;
  cursor: pointer;
  transition: color 0.3s;
  &:hover {
    color: #422d52;
  }
`;

const SubmitButton = styled.button`
  width: 100%;
  background: #533d66;
  color: white;
  padding: 0.625rem 1rem;
  border-radius: 0.5rem;
  font-weight: 500;
  border: none;
  cursor: pointer;
  transition: background 0.3s ease;
  &:hover {
    background: #422d52;
  }
`;

const BackLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  color: #533d66;
  text-decoration: none;
  transition: color 0.3s ease;
  &:hover {
    color: #422d52;
  }
`;

//
// Login With OTP Page Component
//
const VerifyLoginOtpPage = () => {
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [timer, setTimer] = useState(30);
  const [isResendActive, setIsResendActive] = useState(false);

  useEffect(() => {
    let interval;
    if (timer > 0 && !isResendActive) {
      interval = setInterval(() => {
        setTimer((prev) => prev - 1);
      }, 1000);
    } else if (timer === 0) {
      setIsResendActive(true);
    }
    return () => clearInterval(interval);
  }, [timer, isResendActive]);

  const handleOtpChange = (index, value) => {
    if (value.length <= 1 && /^\d*$/.test(value)) {
      const newOtp = [...otp];
      newOtp[index] = value;
      setOtp(newOtp);
      if (value && index < 5) {
        const nextInput = document.getElementById(`otp-${index + 1}`);
        nextInput?.focus();
      }
    }
  };

  const handleKeyDown = (e, index) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      const prevInput = document.getElementById(`otp-${index - 1}`);
      prevInput?.focus();
    }
  };

  const handleResendCode = () => {
    if (isResendActive) {
      setTimer(30);
      setIsResendActive(false);
      // Add resend OTP logic here
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    console.log('OTP:', otp.join(''));
  };

  return (
    <PageContainer>
      {/* Left Section */}
      <LoginLeftSide />
      {/* Right Section */}
      <RightSection>
        <FormWrapper>
          <FormHeader>
            <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
              <div
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '4rem',
                  height: '4rem',
                  background: '#ede9fe', // bg-purple-100
                  borderRadius: '50%',
                  marginBottom: '1rem'
                }}
              >
                <Mail size={32} color="#7e22ce" />
              </div>
              <FormTitle>Verify Your Email</FormTitle>
              <FormSubtitle>
                Please enter the 6-digit code sent to<br />
                <strong style={{ color: '#1f2937' }}>user@example.com</strong>
              </FormSubtitle>
            </div>
          </FormHeader>

          <Card>
            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
              <OTPInputsContainer>
                {otp.map((digit, index) => (
                  <OTPInput
                    key={index}
                    id={`otp-${index}`}
                    type="text"
                    maxLength={1}
                    value={digit}
                    onChange={(e) => handleOtpChange(index, e.target.value)}
                    onKeyDown={(e) => handleKeyDown(e, index)}
                  />
                ))}
              </OTPInputsContainer>
              <ResendContainer>
                Didn't receive the code?{' '}
                {isResendActive ? (
                  <ResendButton type="button" onClick={handleResendCode}>
                    Resend Code
                  </ResendButton>
                ) : (
                  <span>Resend code in {timer}s</span>
                )}
              </ResendContainer>
              <SubmitButton type="submit">
                <span>Verify Code</span>
              </SubmitButton>
            </form>
            <div style={{ textAlign: 'center', marginTop: '1rem' }}>
              <BackLink href="#">
                <ArrowLeft size={16} />
                Back
              </BackLink>
            </div>
          </Card>
        </FormWrapper>
      </RightSection>
    </PageContainer>
  );
};

export default VerifyLoginOtpPage;
